import { endOfMonth, format, startOfMonth } from "date-fns"
import type { TFunction } from "i18next"

import { colors } from "@/components/colors"
import { ReportStatus } from "@/types/report"
import { ActivityTypeKey } from "@/types/activity"
import type { ReadActivityDetailResponse } from "@/types/activity"
import type { ReadFilteredActivitiesParams } from "@/app/api/activity"
import type { ActivityFilters } from "@/app/activity/store/activityCommonStore"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { DATE_FORMAT } from "@/constants/date"

/**
 * 액티비티 목록 필터(store) → /activities/filtered 쿼리 파라미터 변환.
 * 빈 배열 = "전체"(필터 없음) → 해당 파라미터 미전송(undefined).
 * type/seasonWeek/matchDay/player 4종 모두 백엔드에서 필터링된다.
 */
export const buildFilteredActivitiesParams = (
  filters: ActivityFilters,
  start: number,
  end: number,
): ReadFilteredActivitiesParams => ({
  start,
  end,
  type: filters.types.length ? filters.types.join(",") : undefined,
  idfPlayer: filters.playerIds.length ? filters.playerIds.join(",") : undefined,
  idfSeasonWeek: filters.seasonWeekIds.length ? filters.seasonWeekIds.join(",") : undefined,
  matchDay: filters.matchDays.length ? filters.matchDays.join(",") : undefined,
})

/**
 * 액티비티 → Daily Report 진입 URL. 리포트 페이지의 URL→filter 동기화가 자동 렌더하도록
 * 날짜·타입·세션·선수 파라미터를 모두 포함한다 (idfActivity만 보내면 미렌더 — WEB-2242).
 */
export const buildDailyReportUrl = (
  idfActivity: number,
  detail?: ReadActivityDetailResponse | null,
) => {
  let result = `/report/daily?idfActivity=${idfActivity}`

  if (!detail) {
    return result
  }

  if (detail.dateStart) {
    result += `&dateStart=${format(startOfMonth(detail.dateStart), DATE_FORMAT)}`
  }

  if (detail.dateFinish) {
    result += `&dateFinish=${format(endOfMonth(detail.dateFinish), DATE_FORMAT)}`
  }

  if (detail.sessions) {
    const idfSessions = detail.sessions.map((session) => session.idfSession)
    result += `&dailyReportType=`
    result +=
      idfSessions.length > 0
        ? `${ACTIVITY_SESSION_TYPE.SESSION}&idfSession=${idfSessions.join(",")}`
        : ACTIVITY_SESSION_TYPE.ACTIVITY
  }

  if (detail.players) {
    result += `&idfPlayer=${detail.players.map((player) => player.idfPlayer).join(",")}`
  }

  return result
}

/**
 * View Report 진입 가드 (WEB-2241): 분석 완료(ANALYZED) 전에는 버튼 비활성 + 상태 문구.
 * ActivityDetailDialog 의 기존 가드를 공용화 — 스케줄뷰/인포다이얼로그도 동일 패턴 사용.
 */
export const getButtonDisabledByStatus = (status?: ReportStatus) => {
  switch (status) {
    case ReportStatus.UnAnalyzed:
    case ReportStatus.Analyzing:
      return true
    default:
      return false
  }
}

export const getViewReportButtonText = (status: ReportStatus | undefined, t: TFunction) => {
  switch (status) {
    case ReportStatus.UnAnalyzed:
      return t("unAnalyzed")
    case ReportStatus.Analyzing:
      return t("analyzing")
    case ReportStatus.Analyzed:
      return t("button.viewReport")
    default:
      return t("button.viewReport")
  }
}

export const getActivityColor = (type: string) => {
  switch (type) {
    case ActivityTypeKey.training:
      return colors.sky[500]
    case ActivityTypeKey.friendlyMatch:
      return colors.green[600]
    case ActivityTypeKey.league:
      return colors.yellow[400]
    case ActivityTypeKey.cup:
      return colors.brown[600]
    case ActivityTypeKey.rehab:
      return colors.purple[500]
    default:
      return colors.neutral.platinum[500]
  }
}
